<template>
  <div id="sidebar-schedules">
    <v-item-group style="height:80%" class="overflow-y">
      <v-container fluid>
        
        <!-- previous page -->
        <v-row justify="center">
          <v-btn v-if="page !== 1" icon @click="prevPage">
            <v-icon x-large>mdi-chevron-up</v-icon>
          </v-btn>
        </v-row>

        <v-row>
          <v-skeleton-loader v-if="filteredConsultations.length == 0" width="100%" type="list-item, list-item-two-line, list-item-two-line, list-item-two-line"></v-skeleton-loader>
          
          <v-col v-else :class="schedule.status" class="px-0 pb-1 pt-0 schedule-instance" cols="12" v-for="(schedule, index) in filteredConsultations" :key="schedule.id">
            
            <transition name="dropdown">
              
              <v-item v-slot:default="{ active, toggle }">
                  <v-card
                    elevation="0"
                    :class="`align-center ${ active ? 'primary' : 'secondary'}`"
                    @click="setActiveConsultation(index, toggle)"
                  >
                    <v-list>
                      <v-list-item-content :class="`text-center font-weight-medium ${ active ? 'white' : 'blue-grey'}--text text--lighten-1`">
                        
                        <h4>
                          {{ schedule.case.health_concern }}<br>
                          <small>
                            {{ schedule.case.case_number }}
                            <p>{{ schedule.patient.patient.full_name }}</p>
                          </small>

                          <h3 class="type-of-call-label"><p style="text-transform: capitalize;"><strong>{{ schedule.call_method }} Call</strong></p></h3>

                        </h4>

                        <v-list-item-title>{{ displayTime(schedule.call_datetime) }}</v-list-item-title>
                        <v-list-item-subtitle>
                          <span class="text-caption font-weight-light">{{ minuteCounter(schedule.call_datetime) }}</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list>


                    <v-scroll-y-transition>
                      <div v-if="active" class="text-center">
                        <v-menu v-model="landlineMenu" :close-on-content-click="false" :nudge-width="150" offset-x>
                          <v-card>
                            <v-list-item>
                              <v-list-item-avatar>
                                <v-icon color="primary">mdi-phone</v-icon>
                              </v-list-item-avatar>
                            </v-list-item>
                          </v-card>
                        </v-menu>
                        <v-menu v-model="cellularMenu" :close-on-content-click="false" :nudge-width="150" offset-x>
                          <v-card>
                            <v-list-item>
                              <v-list-item-avatar>
                                <v-icon color="primary">mdi-cellphone</v-icon>
                              </v-list-item-avatar>

                              <v-list-item-content>
                                <v-list-item-subtitle v-if="getPatientCellular(schedule.patient)" class="font-weight-bold">
                                  {{ getPatientCellular(schedule.patient) }}
                                </v-list-item-subtitle>
                                <v-list-item-subtitle v-else>
                                  No cellular number
                                </v-list-item-subtitle>
                              </v-list-item-content>
                            </v-list-item>
                          </v-card>
                        </v-menu>
                      </div>
                    </v-scroll-y-transition>
                  </v-card>
              </v-item>
            </transition>
          </v-col>
        </v-row>
        <!-- next page -->
        <v-row justify="center">
          <v-btn
            v-if="page !== lastPage"
            icon
            @click="nextPage"
          >
            <v-icon x-large>mdi-chevron-down</v-icon>
          </v-btn>
        </v-row>
      </v-container>

      <base-confirmation-dialog
        v-model="leaveFormDialog"
        message="Are you sure you want to leave without saving? Any unsaved changes will be lost."
        @confirm="changeToggle()"
      ></base-confirmation-dialog>
    </v-item-group>
  </div>
</template>

<script>
  import moment from 'moment'
  import Pusher from 'pusher-js'

  export default {
    name:'AppSidebarItem',

    components: {
      //
    },

    /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */

    data: ()=> ({
      cellularMenu: false,
      landlineMenu: false,
      page: 1,
      dbDateFormat: "YYYY-MM-DD HH:mm:ss",
      callDialog: false,
      currentIndex: null,
      leaveFormDialog: false,
      toggleChange: null,

      pusher: {
        key: process.env.VUE_APP_PUSHER_KEY,
        cluster: process.env.VUE_APP_PUSHER_CLUSTER,
        secret: process.env.VUE_APP_PUSHER_SECRET,
        appId: process.env.VUE_APP_PUSHER_ID,
      },
    }),

    /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */
    
    computed: {
      requestIndex: {
        get() { return this.$store.getters['request/tests_index'] },
        set(val) { return this.$store.commit('request/setTestsIndex', val) }
      },

      lastPage() { return this.$store.getters['consultation/last_page'] },
      
      cases() { return this.$store.getters['cases/cases'] },
      
      // sidebar items
      consultations() { return this.$store.getters['consultation/consultations'] },
      
      caseInfo() { return this.$store.getters['cases/case'] },
      
      filteredConsultations() {
        return this.$store.getters['consultation/filteredConsultations'];
      }
    },

    /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */

    async mounted() {
      console.log("loaded");
      this.newConsultationListener();
      await this.$store.dispatch('consultation/fetchConsultations', {id: null, doctor: true, today: true, page: this.page});
      
      this.$store.dispatch("diagnosis/getFavoritesMedicalCodes");
      this.$store.dispatch("medicine/getFavoritesMedicines");
      this.$store.dispatch('textreplacement/fetchFullTextReplacements');
      // await this.$store.dispatch('cases/fetchCasesV2', {id: ''});
    },

    methods: {
      /* Pagination */
      async nextPage() {
        await this.page++
        await this.$store.dispatch('consultation/fetchConsultations', {id: null, doctor: true, today: true, status: null, page: this.page});
      },

      /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */

      async prevPage() {
        await this.page--
        await this.$store.dispatch('consultation/fetchConsultations', {id: null, doctor: true, today: true, status: null, page: this.page});
      },

      /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */

      minuteCounter(datetime) {
        let start = moment(datetime, this.dbDateFormat);
        let now = moment();
        let minuteDiff = start.diff(now, 'minutes');
        if (minuteDiff < 0) {
          return "";
        } else {
          let message = 'In ';
          let hour = Math.round(minuteDiff/60);
          if ( hour > 0 )
            message += `${hour}hr `
          let minutes = minuteDiff%60;
          if ( minutes > 0 )
            message += `${minutes}min`
          return message;
        }
      },

      /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */
      
      displayTime(datetime) {
        return moment(datetime, this.dbDateFormat).format('h:mm A')
      },

      /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */

      async setActiveConsultation(index, toggle) {
        console.log("[setActiveConsultation from sidebar]");
        this.toggleChange = toggle();
        
        if (this.currentIndex != null) {
          if (index != this.currentIndex) {
            this.leaveFormDialog = true;
          } else {
            this.setActiveData(index);
          }
        } else {
          toggle();
          this.setActiveData(index);
        }

        this.currentIndex = index;
      },

      /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */

      changeToggle() {
        // this.setActiveConsultation();
        this.setActiveData(this.currentIndex);
      },

      /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */

      async setActiveData(index) {
        console.log("clicked here");
        this.clearPreviousData();

        // consultation
        this.$store.commit('consultation/setActive', index);
        var consultation = this.filteredConsultations[index];
        this.consultation = consultation;
        console.log('[consultation from sidebar setActiveData]', consultation);

        console.log('[consultation patient]', consultation.patient);

        // patient
        this.$store.commit('patient/setPatient', consultation.patient);
        
        console.log("[patient case id]", consultation.patient_case_id);

        // case
        await this.$store.dispatch('cases/fetchCaseV2', consultation.patient_case_id);
        // await this.$store.dispatch('cases/getCase', consultation.patient_case_id);

        var caseInfo = this.caseInfo;

        console.log("getting case information based on clicked consultation schedule")
        console.log(caseInfo);
        
        this.$store.commit('cases/setActiveInfo', caseInfo);
        this.$store.commit('cases/setActiveId', consultation.patient_case_id);

        let patient_emails = this.$store.getters['patient/email_options'];
        console.log("[patient_emails]", patient_emails);
        if(patient_emails.length == 1) {
          console.log("[patient_emails_count]", patient_emails.length);
          this.$store.commit('request/setDestinationEmail', patient_emails[0]);
        }

        await this.loadSavedData(caseInfo, consultation.id, index);
      },

      /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */

      clearPreviousData() {
        this.$store.commit('cases/clearStoreData');
        this.$store.commit('patient/clearStoreData');
        this.$store.commit('examination/clearStoreData');
        this.$store.commit('diagnosis/clearStoreData');
        this.$store.commit('medication/clearStoreData');
        this.$store.commit('recommendation/clearStoreData');
        this.$store.commit('note/clearStoreData');
        this.$store.commit('request/clearStoreData');
      },

      /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */

      async loadSavedData(info, id, index) {
        var ind = info.consultations.findIndex(item => item.id == id)
        var currentConsult = info.consultations[ind]
        console.log('currentConsult', currentConsult)

        var consultation_request = null;
        if(currentConsult.requests && currentConsult.requests.length > 0) {
          consultation_request = currentConsult.requests;
        }

        console.log("consultation request with id", consultation_request +" - "+id);
        
        await this.$store.commit('request/loadSavedData', consultation_request);
        this.requestIndex = index


        console.log("current consult from doctor dashboard");
        console.log(currentConsult);

        console.log("structured current case info");
        console.log(info);

        this.$store.commit('diagnosis/loadSavedData', info.diagnoses);
        this.$store.commit('examination/loadSavedData', info.examinations);
        this.$store.commit('recommendation/loadSavedData', info.recommendations);
        this.$store.commit('medication/loadSavedData', info.medicines);
      },

      newConsultationListener() {
        var me = this;

        var pusher = new Pusher(this.pusher.key, {
          cluster: this.pusher.cluster
        });

        const channel = pusher.subscribe('doctor-case-' + JSON.parse(localStorage.getItem('user')).id);

        console.log("channel", 'doctor-case-' + JSON.parse(localStorage.getItem('user')).id);

        channel.bind('App\\Events\\PatientCaseUpdated', async function(data) {
          await me.$store.dispatch('cases/fetchCase', data.id);
          let caseData = await me.$store.getters['cases/case'];
          console.log('[PatientCaseCreated]', data, caseData.consultations[0].id, 'App\\Events\\PatientCaseUpdated');//
          let consultationIdAlreadyExistsInItems = me.$store.getters['consultation/filteredConsultations'].some(item => item.id == caseData.consultations[0].id);

          console.log(
            "SOCKET_DEBUG",
            {
              function: "newConsultationListener",
              channel: `doctor-case-${JSON.parse(localStorage.getItem('user')).id}`,
              event: "App\\Events\\PatientCaseUpdated",
              data,
              consultationIdAlreadyExistsInItems,
              case: caseData
            }
          )

          if (!consultationIdAlreadyExistsInItems) {
            
            console.log(
              "SOCKET_DEBUG",
              "Before pushing case",
              {
                caseList: me.consultations
              }
            )

            await me.$store.dispatch('consultation/fetchConsultation', {id: caseData.consultations[0].id, patient: caseData.patient, no_info: true});
          }
        });

        // channel.bind('App\\Events\\CaseConsultationUpdated', async function(data) {
        //   console.log('[CaseConsultationUpdated]', data);
        //   await me.$store.dispatch('consultation/fetchConsultationFromList', {caseId: data.caseConsultation.id, id: null, doctor: true, today: true});
        // });
      },

      /* ---------------------------------- FUNCTION SEPARATOR ---------------------------------- */

      getPatientCellular(user) {
        var cellular = ''
        if (user !== null) {
          if (Object.prototype.hasOwnProperty.call(user, 'patient')) {
            cellular = user.patient.mobile_number
          }
        }
        return cellular
      }

    }
  }
</script>

<style lang="scss" scoped>
  .dropdown-enter-active, .dropdown-leave-active {
    transition: height .3s;
  }

  .dropdown-enter, .dropdown-leave-to {
    height: initial;
  }

  .schedule-instance.submitted .theme--light {
    background: #258bf5;
    color: #fff;
  }

  .schedule-instance.submitted * {
    color: #fff;
  }
</style>